import { Injectable } from '@angular/core';

import { ApiQuery, isNotNil, OperandAsString, QueryPart, QueryPartStrategy } from '@stylehaven/components';
import { DIGIHAVEN_PROTOCOL, LOCATION_LEGACY_PROTOCOL } from './constants';
import { IsDateRangeQuery } from './date-range-query-helper';
import { DigihavenFakeQueryPartFields, DigihavenQueryPartsHelper } from './digihaven-query-parts.helper';

@Injectable()
export class WegwijsLuceneHelper {
    private readonly SELECT_ALL_OPERATOR: string = '*:*';
    private readonly FIELD_SEARCH_ESCAPABLE_CHARACTERS: string[] = [
        '+',
        '-',
        '&&',
        '||',
        '!',
        '(',
        ')',
        '{',
        '}',
        '[',
        ']',
        '^',
        `"`,
        '~',
        ':',
        '\\',
        '/'
    ];

    public queryToLuceneQuery(...query: ApiQuery[]): string {
        const allQueryParts = query.flatMap(q => q.queryParts);

        const queryPartsInLuceneSyntax = allQueryParts.flatMap(queryPart => this.getQueryPartAsLuceneQuery(queryPart));

        const restrictions = query
            .map(query => query.restriction)
            .filter(isNotNil)
            .map(restriction =>
                OperandAsString<QueryPart>(restriction, queryPart => this.getQueryPartAsLuceneQuery(queryPart))
            );

        // as a temp workaround, we'll add this selector to select everything so the query can work with NOT queries
        if (this.shouldAddSelectAllOperator(allQueryParts)) {
            queryPartsInLuceneSyntax.push(this.SELECT_ALL_OPERATOR);
        }

        return [...queryPartsInLuceneSyntax, ...restrictions].filter(queryPart => queryPart !== '').join(' ');
    }

    private shouldAddSelectAllOperator(queryParts: QueryPart[]): boolean {
        return (
            queryParts.length > 0 && queryParts.every(queryPart => queryPart.strategy === QueryPartStrategy.MatchNone)
        );
    }

    private getQueryPartAsLuceneQuery(queryPart: QueryPart): string {
        if (!queryPart.hasValues()) {
            return '';
        }

        if (Object.keys(DigihavenFakeQueryPartFields).includes(queryPart.field)) {
            return this.getQueryStringForFakeQueryPart(queryPart);
        }

        switch (queryPart.strategy) {
            case QueryPartStrategy.MatchNone:
                return this.matchNoneStrategy(queryPart.field, queryPart.values);
            case QueryPartStrategy.MatchAtLeastOne:
                return this.matchAtleastOneStrategy(queryPart.field, queryPart.values);
            case QueryPartStrategy.MatchAll:
            default:
                return this.matchAllStrategy(queryPart.field, queryPart.values);
        }
    }

    private matchAllStrategy(field: string, values: string[]): string {
        if (this.isGlobalSearch(field)) {
            const allValues = values.flatMap(value => this.sanitizeValue(value));
            return allValues
                .map(val => {
                    const shouldAddPlusModifier = !val.startsWith('+') && !val.startsWith('-');
                    return shouldAddPlusModifier ? `+${val}` : val;
                })
                .join(' ');
        }

        const fieldPart: string = this.isGlobalSearch(field) ? '' : `${field}:`;
        const query: string = values.map(value => `+${fieldPart}${this.sanitizeValue(value)}`).join(' ');
        return query;
    }

    private matchNoneStrategy(field: string, values: string[]): string {
        if (this.isGlobalSearch(field)) {
            const allValues = values.flatMap(value => this.sanitizeValue(value));
            return `-(${allValues.join(' ')})`;
        }

        const query: string = values.map(value => `${this.sanitizeValue(value)}`).join(' ');
        const fieldPart: string = this.isGlobalSearch(field) ? '' : `${field}:`;
        return `-${fieldPart}(${query})`;
    }

    private matchAtleastOneStrategy(field: string, values: string[]): string {
        if (this.isGlobalSearch(field)) {
            const allValues = values.flatMap(value => this.sanitizeValue(value));
            return `+(${allValues.join(' ')})`;
        }

        const query: string = values.map(value => `${this.sanitizeValue(value)}`).join(' ');
        const fieldPart: string = this.isGlobalSearch(field) ? '' : `${field}:`;
        return `+${fieldPart}(${query})`;
    }

    private getQueryStringForFakeQueryPart(queryPart: QueryPart): string {
        if (queryPart.field === DigihavenFakeQueryPartFields.ChildrenAndCandidateChildren) {
            const queryPartFields: string[] = DigihavenQueryPartsHelper.getActualCorrespondingQueryPartFields(
                queryPart.field
            );
            const queryStrings = queryPartFields.map(
                queryPartField =>
                    `${queryPartField}:${queryPart.values.map(value => this.sanitizeValue(value)).join(' ')}`
            );
            return `+(${queryStrings.join(' ')})`;
        }

        return '';
    }

    private isGlobalSearch(field: string): boolean {
        return field === 'q';
    }

    private sanitizeValue(value: string): string {
        if (IsDateRangeQuery(value)) {
            return value;
        }

        let updatedValue = value;
        updatedValue = this.removeProtocol(value, updatedValue, DIGIHAVEN_PROTOCOL);
        updatedValue = this.removeProtocol(value, updatedValue, LOCATION_LEGACY_PROTOCOL);
        const escaped: string = this.escapeValues(updatedValue, this.FIELD_SEARCH_ESCAPABLE_CHARACTERS);
        return this.shouldPhraseValue(escaped) ? `"${escaped}"` : escaped;
    }

    private removeProtocol(value: string, updatedValue: string, protocol: string) {
        if (value.startsWith(protocol)) {
            const withoutQuery = value.split('?')[0];
            const withoutProtocol: string = withoutQuery.replace(protocol, '');
            updatedValue = `${withoutProtocol}*`;
        }
        return updatedValue;
    }

    protected shouldPhraseValue(value: string) {
        return true;
    }

    private escapeValues(unescapedValue: string, escapableCharacters: string[]): string {
        const escapeCharacters: string[] = escapableCharacters.map(str => `\\${str.split('').join('\\')}`);

        const regExp = new RegExp(escapeCharacters.join('|'), 'gi');
        const escapedValue: string = unescapedValue.replace(regExp, value => this.escapeWithBackslash(value));
        return escapedValue;
    }

    private escapeWithBackslash(value: string): string {
        return `\\${value}`;
    }
}
